import React, { useRef, useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

export const NewVideoSection = () => {
  const videoElementRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });

  const handlePlay = () => {
    if (videoElementRef.current) {
      videoElementRef.current.play();
      setIsPlaying(true);
      setHasStarted(true);
    }
  };

  const handlePause = () => {
    if (videoElementRef.current) {
      videoElementRef.current.pause();
      setIsPlaying(false);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      handlePause();
    } else {
      handlePlay();
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    if (videoElementRef.current) {
      videoElementRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
    <section className="py-20 bg-white relative">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={ref}
          className={`text-center mb-12 transition-all duration-1000 ${inView ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            How to Properly Dispose of Medication
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Watch how NarcX+™ safely and effectively destroys medications on-site.
          </p>
        </div>

        {/* Video Container */}
        <div 
          className={`relative max-w-4xl mx-auto transition-all duration-1000 delay-200 ${inView ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
        >
          {/* Video Wrapper */}
          <div 
            className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 cursor-pointer group"
            onClick={togglePlay}
          >
            {/* Video Element */}
            <video
              ref={videoElementRef}
              className="w-full aspect-video"
              src="https://customer-assets.emergentagent.com/job_safe-disposal-1/artifacts/2p99s3mg_How%20to%20Properly%20Dispose%20of%20Medication%20and%20Drugs.mp4"
              muted={isMuted}
              loop
              playsInline
              preload="auto"
            />

            {/* Play Button Overlay - Always visible when paused */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform duration-300">
                  <Play className="w-8 h-8 text-gray-900 ml-1" />
                </div>
              </div>
            )}

            {/* Controls Bar - Visible on hover when playing */}
            {isPlaying && (
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex items-center justify-between">
                  <button 
                    onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                    className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
                  >
                    <Pause className="w-5 h-5" />
                  </button>
                  
                  <button 
                    onClick={toggleMute}
                    className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all duration-300"
                  >
                    {isMuted ? 
                      <VolumeX className="w-5 h-5" /> : 
                      <Volume2 className="w-5 h-5" />
                    }
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
